<template>
  <div v-if='pageReady' id='pdf-ready'>
    <ExportCover preTitle='Annual Asset Report' :date='new Date()' :title='assetName' />
    <div class='print-page print-portrait x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Asset Report' :date='new Date()' :title='assetName'>
        <h2>Filters Applied</h2>
      </ExportPageHeading>
      <ul class='mt-[5mm] text-sm divide-y border-t border-b'>
        <li class='py-1 text-xs'>
          Year<br />{{ state.filters.year.to }}
        </li>
        <li class='py-1 text-xs'>
          Compare To<br />{{ state.filters.year.from }}
        </li>
      </ul>
    </div>
    <div class='print-page print-landscape x-auto-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Asset Report' :date='new Date()' :title='assetName'>
        <h2>Results</h2>
        <p>Scores for all engagements.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualAssetTable
          :params='params'
          :view='view'
          :isPrint='true'
        />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Asset Report' :date='new Date()' :title='assetName'>
        <h2>Theme Performance</h2>
        <p>The distribution of engagement scores by theme.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualThemePie :params='params' :showKey='true' />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Annual Asset Report' :date='new Date()' :title='assetName'>
        <h2>Change</h2>
        <p>Percentages of engagements that had a positive shift.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphAnnualChangeBar :params='params' />
      </div>
    </div>
    <ExportDisclaimerPage preTitle='Annual Asset Report' :date='new Date()' :title='assetName' />
  </div>
</template>

<script setup>

import { computed, reactive, ref } from 'vue';

import { getStateFromRoute } from '@/router';
import { rehydrateCollections, getExportTitle } from '@/kernel/report';

import ExportCover from '@/components/export/export-cover.vue';
import ExportPageHeading from '@/components/export/export-page-heading.vue';
import ExportDisclaimerPage from '@/components/export/export-disclaimer-page.vue';

import GraphAnnualAssetTable from '@/components/reports/graphs/graph-annual-asset-table.vue';
import GraphAnnualThemePie from '@/components/reports/graphs/graph-annual-theme-pie.vue';
import GraphAnnualChangeBar from '@/components/reports/graphs/graph-annual-change-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const organisationId = computed(() => {
  return getOrganisationId();
});

const filterCollectionMap = [
  {key: 'organisationAsset', collection: 'organisation-asset'},
  {key: 'platformAsset', collection: 'platform-asset'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 1,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      organisationAsset: preload?.filters?.organisationAsset || null,
      platformAsset: preload?.filters?.platformAsset || null
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const params = ref(null);

const assetName = computed(() => {
  return state.filters.organisationAsset?.name || state.filters.platformAsset?.name;
});

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.from === state.filters.year.to) {
    return null;
  }

  if (!state.filters.organisationAsset && !state.filters.platformAsset) {
    return null;
  }

  const args = {
    year: state.filters.year,
    organisationAsset: state.filters.organisationAsset?.id || null,
    platformAsset: state.filters.platformAsset?.id || null
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
  }

  document.title = getExportTitle(`Annual Asset Report - ${assetName.value}`, new Date());

  params.value = calcParams();

  pageReady.value = true;
}

loadState();

</script>
